import * as R from 'ramda';
import trim from 'voca/trim';
import { toastr } from 'react-redux-toastr';
import { getGeocode } from 'use-places-autocomplete';
// constants
import * as GC from '../constants';
// utilities
import { sendRequest } from '../utilities/http';
import endpointsMap from '../utilities/endpoints';
// helpers
import { logToSystem } from './log';
import { handleException } from './ajax';
import { getWindowLocale } from './locale';
import { getPropFromObject } from './getter';
import { isMetricUomSystem } from './config';
import { getCurrentBranchGuid } from './storage';
import { getStateValueByConfig } from './location';
import { renameKeys, mapObjectNullFieldsToEmptyStrings } from './array-object';
import {
  isTrue,
  ifElse,
  isString,
  isNotNil,
  getOrElse,
  callFunction,
  isNilOrEmpty,
  isNotNilAndNotEmpty,
} from './helpers';
//////////////////////////////////////////////////
const GeoPoint = require('geopoint');
//////////////////////////////////////////////////

const { is } = Object;

const makeGoogleAPILinkWithKey = (key) => (
  `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=geometry,drawing,places&language=en`
);

function getGeolocation() {
  return new Promise((resolve, reject) =>
    window.navigator.geolocation.getCurrentPosition(resolve, reject, {
      timeout: 4000,
      enableHighAccuracy: true,
    }));
}


function googleMapsGeocode(coords, componentName) {
  const { geocode } = new google.maps.Geocoder();

  const { latitude, longitude } = coords;

  const location = new google.maps.LatLng(latitude, longitude);

  logToSystem(GC.LOG_MESSAGE_TYPE_GOOGLE_MAPS_GEOCODER, { componentName, from: 'helpers googleMapsGeocode' });

  return new Promise((resolve, reject) => {
    geocode({ location }, ([result], status) => {
      if (is(status, 'OK')) {
        if (isNotNilAndNotEmpty(result)) {
          return resolve(result);
        }

        toastr.info(getWindowLocale('messages:no-results-found', 'No results found'));
      } else {
        toastr.info(getWindowLocale(
          'messages:select-unidentified-location',
          'You have selected a unidentified location. Please, try selecting another location.',
        ));
      }

      reject(status);
    });
  });
}

const getDistanceBetweenTwoPoints = (point1, point2, inKm = false) => {
  const inKilemeters = R.or(inKm, isMetricUomSystem());

  const latitude1 = getPropFromObject(GC.FIELD_LATITUDE, point1);
  const longitude1 = getPropFromObject(GC.FIELD_LONGITUDE, point1);
  const latitude2 = getPropFromObject(GC.FIELD_LATITUDE, point2);
  const longitude2 = getPropFromObject(GC.FIELD_LONGITUDE, point2);

  const point1Geo = new GeoPoint(latitude1, longitude1);
  const point2Geo = new GeoPoint(latitude2, longitude2);

  return point1Geo.distanceTo(point2Geo, inKilemeters);
};

const getSpecificLocationValue = (
  components,
  type,
  alternateType,
) => {
  const addressComponent = R.find((component) => (
    isNotNil(
      R.find((componentType) => (
        R.or(R.equals(type, componentType), R.equals(alternateType, componentType))
      ),
      component.types,
    ))),
    components,
  );

  return getOrElse(addressComponent, 'long_name', '');
};

const getGoogleGeocodeByAddressDataFromResult = (
  result,
  useFormattedAddress,
  addressFieldName,
) => {
  const {
    geometry,
    formatted_address,
    address_components,
  } = result;

  const data = address_components; // eslint-disable-line
  const longitude = geometry.location.lng();
  const latitude = geometry.location.lat();
  const formattedAddress = formatted_address; // eslint-disable-line
  const route = getSpecificLocationValue(data, 'route');
  const country = getSpecificLocationValue(data, 'country');
  const zip = getSpecificLocationValue(data, 'postal_code');
  const streetNumber = getSpecificLocationValue(data, 'street_number');
  const city = getSpecificLocationValue(data, 'locality', 'postal_town');
  const state = getSpecificLocationValue(data, 'administrative_area_level_1');

  const addressString = trim(`${streetNumber} ${route}`);

  const address1 = R.or(isTrue(useFormattedAddress), isNilOrEmpty(addressString)) ? formattedAddress : addressString;

  const resultObject = {
    zip,
    city,
    state,
    country,
    address1,
    latitude,
    longitude,
    formattedAddress,
  };

  return ifElse(
    isString(addressFieldName),
    renameKeys({ 'address1': addressFieldName }, resultObject),
    resultObject,
  );
};

const getGeocodeFieldsFromAPIRes = (
  searchAddress,
  data,
  useFormattedAddress,
  addressFieldName,
) => {
  const {
    zip,
    city,
    country,
    latitude,
    longitude,
    streetName,
    streetNumber,
  } = data;

  const state = getStateValueByConfig(R.prop(GC.FIELD_STATE, data));

  const formattedAddress = trim(searchAddress);

  const addressString = trim(`${streetNumber} ${streetName}`);

  const address1 = R.or(isTrue(useFormattedAddress), isNilOrEmpty(addressString)) ? formattedAddress : addressString;

  const resultObject = {
    zip,
    city,
    state,
    country,
    address1,
    latitude,
    longitude,
    formattedAddress,
  };

  return ifElse(
    isString(addressFieldName),
    renameKeys({ 'address1': addressFieldName }, resultObject),
    resultObject,
  );
};

// API use Google API first
const geocodeByPlaceAddress = async (
  address,
  componentName,
  catchCallback,
  options,
) => {
  try {
    logToSystem(
      GC.LOG_MESSAGE_TYPE_DISTANCE_SERVICE_GEOFENCING_ADDRESS,
      {
        address,
        componentName,
        from: 'helpers geocodeByPlaceAddress',
      },
    );

    const useFormattedAddress = R.path(['useFormattedAddress'], options);
    const addressFieldName = R.path(['addressFieldName'], options);

    // TODO: with config or remove condition logic
    const useAPI = true;

    if (isTrue(useAPI)) {
      const enterpriseGuid = getCurrentBranchGuid();

      const { data } = await sendRequest(
        'post',
        endpointsMap.distanceGeofencingLocationInfoByAddress,
        { data: { address, enterpriseGuid } },
      );

      return getGeocodeFieldsFromAPIRes(
        address,
        mapObjectNullFieldsToEmptyStrings(data),
        useFormattedAddress,
        addressFieldName,
      );
    }

    if (isString(componentName)) {
      logToSystem(GC.LOG_MESSAGE_TYPE_GOOGLE_GEOCODE_API, { componentName, from: 'helpers geocodeByPlaceAddress' });
    }

    const results = await getGeocode({ address });

    return getGoogleGeocodeByAddressDataFromResult(R.head(results), useFormattedAddress, addressFieldName);
  } catch (error) {
    callFunction(catchCallback);

    handleException(error, 'geocodeByPlaceAddress');
  }
};

// API use Amazon API first
const geocodeByPlaceAddressV2 = async (
  address,
  componentName,
  catchCallback,
  options,
) => {
  try {
    logToSystem(
      GC.LOG_MESSAGE_TYPE_DISTANCE_SERVICE_GEOFENCING_ADDRESS,
      {
        address,
        componentName,
        from: 'helpers geocodeByPlaceAddressV2',
      },
    );

    const useFormattedAddress = R.path(['useFormattedAddress'], options);
    const addressFieldName = R.path(['addressFieldName'], options);

    const enterpriseGuid = getCurrentBranchGuid();

    const { data } = await sendRequest(
      'post',
      endpointsMap.distanceGeofencingLocationInfoByAddressV2,
      { data: { address, enterpriseGuid } },
    );

    return getGeocodeFieldsFromAPIRes(
      address,
      mapObjectNullFieldsToEmptyStrings(data),
      useFormattedAddress,
      addressFieldName,
    );
  } catch (error) {
    callFunction(catchCallback);

    handleException(error, 'geocodeByPlaceAddressV2');
  }
};


const geocodeByPlaceAddressWithTokenAndEnterpriseGuid = async (
  address,
  componentName,
  catchCallback,
  options,
) => {
  try {
    logToSystem(
      GC.LOG_MESSAGE_TYPE_DISTANCE_SERVICE_GEOFENCING_ADDRESS,
      {
        componentName,
        from: 'helpers geocodeByPlaceAddressWithTokenAndEnterpriceGuid',
      },
    );

    const carrierToken = R.path(['carrierToken'], options);
    const enterpriseGuid = R.path(['enterpriseGuid'], options);
    const addressFieldName = R.path(['addressFieldName'], options);
    const useFormattedAddress = R.path(['useFormattedAddress'], options);

    const { data } = await sendRequest(
      'post',
      endpointsMap.getTelLocationInfoByAddress,
      {
        headers: { carrierToken },
        data: { address, enterpriseGuid },
      },
    );

    return getGeocodeFieldsFromAPIRes(
      address,
      mapObjectNullFieldsToEmptyStrings(data),
      useFormattedAddress,
      addressFieldName,
    );
  } catch (error) {
    callFunction(catchCallback);

    handleException(error, 'geocodeByPlaceAddressWithTokenAndEnterpriceGuid');
  }
};

const getDistancePerDayUomLocale = () => {
  if (isMetricUomSystem()) return getWindowLocale('titles:kilometers-per-day', 'Kilometers per day');

  return getWindowLocale('titles:miles-per-day', 'Miles per day');
};

export {
  getGeolocation,
  googleMapsGeocode,
  geocodeByPlaceAddress,
  geocodeByPlaceAddressV2,
  makeGoogleAPILinkWithKey,
  getSpecificLocationValue,
  getDistancePerDayUomLocale,
  getGeocodeFieldsFromAPIRes,
  getDistanceBetweenTwoPoints,
  getGoogleGeocodeByAddressDataFromResult,
  geocodeByPlaceAddressWithTokenAndEnterpriseGuid,
};
